import { createSlice } from '@reduxjs/toolkit';

import { copyProjectInfo } from '@/features/projects/projectsSlice';
import type { HotelInfo, RoomStats } from '@/features/projects/projectsSlice';

// Project groups (Task-80). A group is a name plus the settings every member
// project inherits. Membership lives on the PROJECT (`HotelInfo.group`), never
// on the group record: the server computes `projects[]` for the reader and has
// no member list to post back.

// Everything a member inherits, one key per setting. The backend adds keys
// here over time; the editor renders the ones listed in groupSettingDefs.
export interface ProjectGroupSettings {
  chain_manager:	string[];	// global usernames; never null
}

export interface ProjectGroup {
  id:				string;		// uuid, minted by the server on create
  name:				string;
  settings:			ProjectGroupSettings;
}

export interface ProjectGroupWithStats extends ProjectGroup {
  projects:			string[];	// the members THE CALLER MAY SEE
  stats: {
    rooms:			RoomStats;	// summed server-side over those members
  };
}

// POST /project_groups.json. On an update a settings key that is left out
// keeps the stored value, [] clears it (§2.3) — so only the keys the user
// could edit may be sent.
export interface ProjectGroupRecord {
  is_new?:			boolean;
  to_delete?:		boolean;	// only an EMPTY group
  disband?:			boolean;	// a group WITH projects, settings cleared first
  id?:				string;
  name?:			string;
  settings?:		Partial<ProjectGroupSettings>;
}

export interface ProjectGroupsHolder {
  groups:			ProjectGroupWithStats[];
  // The group the overview is drilled into, and its members from
  // /hotels.json?group=. Kept apart from projects.projects on purpose: that
  // list is the flat estate the header tiles and the project page read.
  active:			string | null;
  members:			HotelInfo[] | null;
}

export const projectGroupsUrl = "/project_groups.json";

export const PROJECT_GROUP_NAME_MAX = 100;

// One entry per inheritable setting: which privilege edits it (§2.4).
export interface GroupSettingDef {
  key:				keyof ProjectGroupSettings;
  privilege:		string;
}

export const groupSettingDefs: GroupSettingDef[] = [
  { key: 'chain_manager',	privilege: 'SetChainmanager' },
];

const initialState: ProjectGroupsHolder = {
  groups:			[],
  active:			null,
  members:			null,
};

export const projectGroupsSlice = createSlice({
  name: 'projectGroups',
  initialState,
  reducers: {
    setGroups: (state, action) => {
      state.groups = action.payload;
    },
    setActive: (state, action) => {
      state.active = action.payload.active;
      state.members = action.payload.members;
    },
  },
});

export function getProjectGroupsUrl(id?: string): string {
  return	id
			?	projectGroupsUrl + "?id=" + encodeURIComponent(id)
			:	projectGroupsUrl;
}

// The list arrives in creation order; the UI shows it by name.
export function parseProjectGroups(result: any): ProjectGroupWithStats[] {
  const groups: ProjectGroupWithStats[] =	result && Array.isArray(result.project_groups)
											?	result.project_groups
											:	[];

  return [...groups].sort((a, b) => a.name.localeCompare(b.name));
}

export function getProjectGroupById(id: string | undefined, groups: ProjectGroupWithStats[]): ProjectGroupWithStats | null {
  if (!id)
    return null;

  return groups.find((g) => g.id === id) || null;
}

export function getEmptyGroupSettings(): ProjectGroupSettings {
  return {
    chain_manager:	[],
  };
}

export function copyGroupSettings(settings: ProjectGroupSettings | null | undefined): ProjectGroupSettings {
  return {
    ...getEmptyGroupSettings(),
    chain_manager:	settings && settings.chain_manager
    				?	[...settings.chain_manager]
    				:	[],
  };
}

// A group still "carries settings" when any inheritable list is non-empty —
// the server refuses to disband it then (§3.2).
export function groupHasSettings(settings: ProjectGroupSettings | null | undefined): boolean {
  if (!settings)
    return false;

  return groupSettingDefs.some((def) => (settings[def.key] || []).length > 0);
}

// Client-side mirror of the server's name rules (§4.6). Returns the
// translation key of the first failure, or null.
export function validateGroupName(name: string, id: string | undefined, groups: ProjectGroup[]): string | null {
  const trimmed = (name || "").trim();

  if (trimmed === "")
    return 'projects:groups:err_empty_name';

  if (trimmed.length > PROJECT_GROUP_NAME_MAX)
    return 'projects:groups:err_long_name';

  const lower = trimmed.toLowerCase();
  if (groups.some((g) => g.id !== id && g.name.trim().toLowerCase() === lower))
    return 'projects:groups:err_dup_name';

  return null;
}

// The create / update body. `editable` names the settings keys the user could
// change; every other key is left out so the server keeps its stored value.
export function buildGroupRecord(id: string | undefined, name: string, settings: ProjectGroupSettings, editable: (keyof ProjectGroupSettings)[]): ProjectGroupRecord {
  const sent: Partial<ProjectGroupSettings> = {};
  for (const key of editable)
    sent[key] = [...(settings[key] || [])];

  const record: ProjectGroupRecord =	id
  										?	{ id: id, name: name.trim() }
  										:	{ is_new: true, name: name.trim() };

  if (Object.keys(sent).length > 0)
    record.settings = sent;

  return record;
}

// One button, chosen by state (§4.2): DELETE for an empty group, DISBAND for
// one with projects — and DISBAND only once its settings are cleared.
export function groupRemoveMode(group: ProjectGroupWithStats): 'delete' | 'disband' {
  return (group.projects || []).length === 0 ? 'delete' : 'disband';
}

export function getRemoveGroupRecord(group: ProjectGroupWithStats): ProjectGroupRecord {
  return	groupRemoveMode(group) === 'delete'
  			?	{ to_delete: true, id: group.id }
  			:	{ disband: true, id: group.id };
}

// The editor's project picker against the members the server reported: who
// joins and who leaves on save. Order follows the picker.
export function diffGroupMembers(before: string[], after: string[]): { added: string[], removed: string[] } {
  const was = new Set(before);
  const now = new Set(after);

  return {
    added:		after.filter((id) => !was.has(id)),
    removed:	before.filter((id) => !now.has(id)),
  };
}

// Moving a project is a PROJECT save (§2.1, §3.4): the project as it is, plus
// group — "<id>" joins (leaving any previous group), "" leaves.
export function getMoveProjectBody(proj: HotelInfo, groupId: string): HotelInfo {
  const body = copyProjectInfo(proj);

  body.old_id = body.old_id || body.id;
  body.group = groupId;

  return body;
}

// The overview's top level: every group the caller sees, plus the projects
// that are not in one of THOSE groups. A project whose group is missing from
// the list (deleted meanwhile, or not visible) is shown as ungrouped (§4.1).
export function getUngroupedProjects(projects: HotelInfo[], groups: ProjectGroup[]): HotelInfo[] {
  const known = new Set(groups.map((g) => g.id));

  return projects.filter((p) => !p.group || !known.has(p.group));
}

// The drill-down's header tiles: the members' numbers added up, the same sums
// projectsSlice.setProjects makes for the flat list.
export function sumRoomStats(projects: HotelInfo[]): RoomStats {
  const stats: RoomStats = {
    allocated:	0,
    activated:	0,
    connected:	0,
    created:	0,
  };

  for (const proj of projects) {
    if (!proj.stats)
      continue;

    stats.allocated += proj.stats.rooms.allocated;
    stats.activated += proj.stats.rooms.activated;
    stats.connected += proj.stats.rooms.connected;
    stats.created += proj.stats.rooms.created;
  }

  return stats;
}
